import Swal from "sweetalert2";
import { getHTMLForSummaryPopup, getHTMLForTablePopup } from '../../utils/PopupUtils';

export function showDcGroupPop(item){
  if (!item || !item.id) {
    Swal.fire('Please select a delivery challan first.', '', 'info');
    return;
  }

  const summaryHtml = getHTMLForSummaryPopup(
    'DC GROUP DETAILS',
    [
      {label: 'BILLING NAME', value: item.customer_name},
      {label: 'SO NO', value: item.sales_order_no},
      {label: 'CONSIGNEE', value: item.consignee_name},
      {label: 'PROJECT/SITE', value: item.project_name},
      {label: 'DC DATE', value: item.delivery_challan_date},
      {label: 'GRADE', value: item.product_name},
      {label: 'REPORT NO', value: item.report_no},
      {label: 'GRADE NO', value: item.grade_number},
      {label: 'TOTAL DC QTY', value: item.total_dc_qty},
      {label: 'DC COUNT', value: item.dc_count || (item.dc_ids_list || []).length},
      {label: 'DC NOS', value: item.dc_nos},
      {label: '7 DAY GENERATED', value: (item.has_7_day === true || item.has_7_day === 'Yes') ? 'Yes' : 'No'},
      {label: '28 DAY GENERATED', value: (item.has_28_day === true || item.has_28_day === 'Yes') ? 'Yes' : 'No'},
    ]
  );

  const dcRows = (item.dc_details || []).map((dc) => ({
    delivery_challan_no: dc.delivery_challan_no,
    dc_qty: dc.dc_qty,
    vehicle_no: dc.vehicle_no || '-',
  }));

  const tableHtml = getHTMLForTablePopup(
    'LINKED DELIVERY CHALLANS',
    [
      {key: 'delivery_challan_no', label: 'DC No'},
      {key: 'dc_qty', label: 'Qty', align: 'right'},
      {key: 'vehicle_no', label: 'Vehicle'},
    ],
    dcRows,
    null,
    'No delivery challans linked.'
  );

  Swal.fire({
    width: 720,
    html: `${summaryHtml}<div style="height:12px;"></div>${tableHtml}`,
  });
}

/** Age/test is fulfilled only when a real load/strength has been entered. */
export function isCubeTestValueFulfilled(value) {
  return parseFloat(value || 0) > 0;
}

/**
 * Compare measured strength to reference.
 * Zero / blank values mean not fulfilled yet (PENDING), not FAIL.
 */
export function getCubeStrengthResult(measuredValue, referenceValue) {
  if (!isCubeTestValueFulfilled(measuredValue)) {
    return 'PENDING';
  }
  if (parseFloat(measuredValue) >= parseFloat(referenceValue || 0)) {
    return 'PASS';
  }
  return 'FAIL';
}

export function getCubeResultColor(result) {
  if (result === 'FAIL') return 'red';
  if (result === 'PENDING') return '#f97316';
  return 'green';
}

/** Customer initials from name — same pattern as Solar Readymix. */
export function buildCustomerInitials(customerName = '') {
  if (!customerName) {
    return '';
  }
  return String(customerName)
    .trim()
    .split(/\s+/)
    .flatMap((word) => {
      const cleaned = word.replace(/[^a-zA-Z0-9&]/g, '');
      if (!cleaned) {
        return [];
      }
      if (cleaned.includes('&')) {
        return cleaned.split('&').filter(Boolean).map((part) => part.charAt(0));
      }
      return [cleaned.charAt(0)];
    })
    .join('')
    .toUpperCase();
}

export function buildGradePart(gradeName = '') {
  if (!gradeName) {
    return '';
  }
  return String(gradeName).replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
}

/** DDMMYY from YYYY-MM-DD or DD-MM-YYYY. */
export function formatDcDateForCubeId(dateInput = '') {
  const raw = String(dateInput || '').trim();
  if (!raw) {
    return '';
  }
  const parts = raw.split(/[-/]/);
  if (parts.length !== 3) {
    return raw.replace(/\D/g, '').slice(0, 6);
  }
  if (parts[0].length === 4) {
    return `${parts[2].padStart(2, '0')}${parts[1].padStart(2, '0')}${parts[0].slice(-2)}`;
  }
  return `${parts[0].padStart(2, '0')}${parts[1].padStart(2, '0')}${parts[2].slice(-2)}`;
}

/**
 * Cube ID: customer initials + grade + age + DC date (DDMMYY).
 * Example: NIM + M30 + 07 + 170726 -> NIM3007170726
 */
export function buildCubeId({ customerName, gradeName, ageDays, dcDateInput }) {
  const initials = buildCustomerInitials(customerName);
  const gradePart = buildGradePart(gradeName);
  const agePart = ageDays != null && ageDays !== ''
    ? String(ageDays).padStart(2, '0')
    : '';
  const datePart = formatDcDateForCubeId(dcDateInput);
  return `${initials}${gradePart}${agePart}${datePart}`.slice(0, 19);
}

export function buildCubeIdFromSelection(selection, ageDays, dcDateInput) {
  if (!selection?.customer_name) {
    return '';
  }
  return buildCubeId({
    customerName: selection.customer_name,
    gradeName: selection.product_name,
    ageDays,
    dcDateInput: dcDateInput || selection.delivery_challan_date || selection.casting_dt,
  });
}

export function buildCubeSampleId(baseId, index, total) {
  const clean = String(baseId || '').replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
  if (!clean) {
    return '';
  }
  if (total <= 1) {
    return clean.slice(0, 20);
  }
  return `${clean}${index}`.slice(0, 20);
}

export function syncCubeIdsForAgeRows(rowsData, selection, dcDateInput) {
  if (!selection?.customer_name || !rowsData?.length) {
    return rowsData;
  }
  const dcDate = dcDateInput || selection.delivery_challan_date || selection.casting_dt;
  return rowsData.map((row) => ({
    ...row,
    cube_id: buildCubeIdFromSelection(selection, row.age, dcDate),
  }));
}

export function applyAgeRowFieldChange(rowsData, index, name, value, selection, dcDateInput) {
  const rowsInput = [...rowsData];
  rowsInput[index][name] = value;
  if (name === 'age' && selection?.customer_name) {
    rowsInput[index].cube_id = buildCubeIdFromSelection(
      selection,
      value,
      dcDateInput || selection.delivery_challan_date || selection.casting_dt,
    );
  }
  return rowsInput;
}

export function buildPendingAgeRow(ageDays, cubeId = '', curedPlace = 'plant') {
  return {
    age: ageDays,
    no_of_moulds: 0,
    cube_id: cubeId,
    cured_place: curedPlace,
  };
}

export function resolveNextAgeDays(existingRows, pendingAgeDays) {
  const ages = new Set((existingRows || []).map((row) => Number(row.age)));
  if (!ages.has(7)) {
    return 7;
  }
  if (!ages.has(28)) {
    return 28;
  }
  return pendingAgeDays || 7;
}

export function resolveCuredPlaceFromAgeRows(rowsData, fallback = '') {
  const rowWithPlace = (rowsData || []).find((row) => row.cured_place);
  return rowWithPlace?.cured_place || fallback;
}

/** YYYY-MM-DD casting date from API display date DD-MM-YYYY. */
export function castingDateInputFromApi(displayDate) {
  if (!displayDate) {
    return '';
  }
  const parts = String(displayDate).split('-');
  if (parts.length === 3 && parts[0].length === 4) {
    return displayDate;
  }
  if (parts.length === 3) {
    return `${parts[2]}-${parts[1]}-${parts[0]}`;
  }
  return '';
}

/** Report date = casting date + age days (YYYY-MM-DD). */
export function reportDateFromCastingAndAge(castingDateInput, ageDays) {
  if (!castingDateInput || !ageDays) {
    return '';
  }
  const parsed = new Date(`${castingDateInput}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) {
    return '';
  }
  parsed.setDate(parsed.getDate() + Number(ageDays));
  const year = parsed.getFullYear();
  const month = String(parsed.getMonth() + 1).padStart(2, '0');
  const day = String(parsed.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function buildCsrRowsFromAgeSetup(rowsData, castingDateInput, mode = 'create') {
  if (!castingDateInput || !rowsData?.length) {
    return [];
  }

  const newCsrRows = [];
  let serialNumber = 1;

  rowsData.forEach((row) => {
    const { age, no_of_moulds, cube_id: cubeId } = row;
    const moulds = parseInt(no_of_moulds, 10) || 0;
    if (!moulds) {
      return;
    }

    const testingDate = new Date(`${castingDateInput}T00:00:00`);
    testingDate.setDate(testingDate.getDate() + Number(age));
    const year = testingDate.getFullYear();
    const month = String(testingDate.getMonth() + 1).padStart(2, '0');
    const day = String(testingDate.getDate()).padStart(2, '0');
    const testingDateValue = `${year}-${month}-${day}`;

    for (let i = 1; i <= moulds; i += 1) {
      const sampleId = buildCubeSampleId(cubeId, i, moulds);
      if (mode === 'edit') {
        newCsrRows.push({
          id: null,
          sl_no: serialNumber,
          age: parseInt(age, 10),
          sample_id: sampleId,
          age_date: testingDateValue,
          no_of_rows: moulds,
          act_weight: 0,
          act_load: 0,
          cus_weight: 0,
          cus_load: 0,
          act_strength: 0,
          ref_cube_strength: 0,
          act_difference: 0,
          cus_strength: 0,
          cus_difference: 0,
          act_average: 0,
          cus_average: 0,
          act_result: 'PENDING',
          act_strength_result: 'PENDING',
          cus_result: 'PENDING',
          cus_strength_result: 'PENDING',
          user_remarks: '',
        });
      } else {
        newCsrRows.push({
          sl_no: serialNumber,
          age: parseInt(age, 10),
          cube_id: sampleId,
          testing_date: testingDateValue,
          no_of_rows: moulds,
          act_weight: 0,
          act_load: 0,
          cus_weight: 0,
          cus_load: 0,
          act_strength: 0,
          ref_cube_strength: 0,
          act_difference: 0,
          cus_strength: 0,
          cus_difference: 0,
          act_result: 'PENDING',
          act_strength_result: 'PENDING',
          cus_result: 'PENDING',
          cus_strength_result: 'PENDING',
        });
      }
      serialNumber += 1;
    }
  });

  return newCsrRows;
}

/** Replace CSR rows for one age only; keep other ages (and their entered values). */
export function mergeCsrRowsForAge(existingCsrRows, newRows, targetAge) {
  const ageNum = Number(targetAge);
  const retained = (existingCsrRows || []).filter((row) => Number(row.age) !== ageNum);
  const merged = [...retained, ...(newRows || [])];
  return merged.map((row, index) => ({
    ...row,
    sl_no: index + 1,
  }));
}

export function getCsrRowsForAge(existingCsrRows, targetAge) {
  const ageNum = Number(targetAge);
  return (existingCsrRows || []).filter((row) => Number(row.age) === ageNum);
}

function applyGeneratedMetaToExistingRow(existingRow, generatedRow, mouldCount) {
  if (!generatedRow) {
    return { ...existingRow, no_of_rows: mouldCount };
  }
  return {
    ...existingRow,
    no_of_rows: mouldCount,
    ...(generatedRow.sample_id != null ? { sample_id: generatedRow.sample_id } : {}),
    ...(generatedRow.cube_id != null ? { cube_id: generatedRow.cube_id } : {}),
    ...(generatedRow.age_date != null ? { age_date: generatedRow.age_date } : {}),
    ...(generatedRow.testing_date != null ? { testing_date: generatedRow.testing_date } : {}),
  };
}

/**
 * Merge generated rows for one age while preserving entered values on existing rows.
 * - Same count: keep all existing data, refresh ids/dates/no_of_rows only.
 * - Increase: keep existing rows, append empty generated rows.
 * - Decrease: keep first N existing rows with their data.
 */
export function mergeCsrRowsForAgePreservingData(existingCsrRows, generatedRows, targetAge) {
  const ageNum = Number(targetAge);
  const retainedOtherAges = (existingCsrRows || []).filter((row) => Number(row.age) !== ageNum);
  const existingForAge = getCsrRowsForAge(existingCsrRows, targetAge);
  const generated = generatedRows || [];
  const mouldCount = generated.length;

  let mergedForAge;
  if (!existingForAge.length) {
    mergedForAge = generated;
  } else if (mouldCount === existingForAge.length) {
    mergedForAge = existingForAge.map((existing, index) =>
      applyGeneratedMetaToExistingRow(existing, generated[index], mouldCount),
    );
  } else if (mouldCount > existingForAge.length) {
    const kept = existingForAge.map((existing, index) =>
      applyGeneratedMetaToExistingRow(existing, generated[index], mouldCount),
    );
    const appended = generated.slice(existingForAge.length).map((row) => ({
      ...row,
      no_of_rows: mouldCount,
    }));
    mergedForAge = [...kept, ...appended];
  } else {
    mergedForAge = existingForAge.slice(0, mouldCount).map((existing, index) =>
      applyGeneratedMetaToExistingRow(existing, generated[index], mouldCount),
    );
  }

  const merged = [...retainedOtherAges, ...mergedForAge];
  return merged.map((row, index) => ({
    ...row,
    sl_no: index + 1,
  }));
}

/**
 * Confirm when mould count changes, then merge generated rows without wiping existing values.
 */
export async function generateAndMergeCsrRowsForAge({
  existingCsrRows,
  ageRow,
  castingDateInput,
  mode = 'create',
}) {
  const generated = buildCsrRowsFromAgeSetup([ageRow], castingDateInput, mode);
  if (!generated.length) {
    return { merged: existingCsrRows, cancelled: false, empty: true };
  }

  const existingForAge = getCsrRowsForAge(existingCsrRows, ageRow.age);
  const existingCount = existingForAge.length;
  const newCount = generated.length;

  if (existingCount > 0 && newCount !== existingCount) {
    const increasing = newCount > existingCount;
    const result = await Swal.fire({
      title: increasing
        ? `Increase mould rows from ${existingCount} to ${newCount}?`
        : `Decrease mould rows from ${existingCount} to ${newCount}?`,
      text: increasing
        ? 'Existing row values will be kept. New row(s) will be added empty.'
        : `The last ${existingCount - newCount} row(s) will be removed. Remaining values will be kept.`,
      showCancelButton: true,
      confirmButtonText: 'Yes',
      cancelButtonText: 'No',
    });
    if (!result.isConfirmed) {
      return { merged: existingCsrRows, cancelled: true, empty: false };
    }
  }

  return {
    merged: mergeCsrRowsForAgePreservingData(existingCsrRows, generated, ageRow.age),
    cancelled: false,
    empty: false,
  };
}

function toNumber(value) {
  const parsed = parseFloat(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

/**
 * Build API report_list rows and always recompute age averages from strengths
 * so list "7th/28th Day Avg" matches what the form shows.
 */
export function buildReportListPayload(csrRows, options = {}) {
  const { mode = 'create', formatDate } = options;
  const rows = csrRows || [];
  const avgByAge = {};

  rows.forEach((row) => {
    const age = parseInt(row.age, 10) || 0;
    if (!avgByAge[age]) {
      avgByAge[age] = { actTotal: 0, cusTotal: 0, count: 0 };
    }
    avgByAge[age].actTotal += toNumber(row.act_strength);
    avgByAge[age].cusTotal += toNumber(row.cus_strength);
    avgByAge[age].count += 1;
  });

  Object.keys(avgByAge).forEach((age) => {
    const group = avgByAge[age];
    group.actAverage = group.count ? Number((group.actTotal / group.count).toFixed(2)) : 0;
    group.cusAverage = group.count ? Number((group.cusTotal / group.count).toFixed(2)) : 0;
  });

  return rows.map((q) => {
    const age = parseInt(q.age, 10) || 0;
    const group = avgByAge[age] || { actAverage: 0, cusAverage: 0 };
    const actStrength = toNumber(q.act_strength);
    const cusStrength = toNumber(q.cus_strength);
    const actAverage = group.actAverage;
    const cusAverage = group.cusAverage;
    const dateValue = mode === 'edit' ? q.age_date : q.testing_date;
    const payload = {
      age,
      age_date: formatDate ? formatDate(dateValue) : dateValue,
      sample_id: mode === 'edit' ? q.sample_id : q.cube_id,
      act_weight: toNumber(q.act_weight),
      act_load: toNumber(q.act_load),
      act_strength: actStrength,
      act_average: actAverage,
      act_difference: Number((actAverage - actStrength).toFixed(2)),
      act_result: q.act_result || 'PENDING',
      cus_weight: toNumber(q.cus_weight),
      cus_load: toNumber(q.cus_load),
      cus_strength: cusStrength,
      cus_average: cusAverage,
      cus_difference: Number((cusAverage - cusStrength).toFixed(2)),
      cus_result: q.cus_result || 'PENDING',
      user_remarks: mode === 'edit' ? (q.user_remarks || '') : (q.remarks_dtl || ''),
    };
    if (mode === 'edit') {
      payload.id = q.id ? parseInt(q.id, 10) : null;
    }
    return payload;
  });
}

